import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { generatePath, useHistory } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/ar";

import TruncatedWithTooltip from "../../../../../components/TruncatedWithTooltip";
import { UIAvatar, UIStatusTag, UITable } from "../../../../../components/ui";
import { ROUTE_PATHS } from "../../../../../utils/RoutesPaths";

const EMPTY_VALUE = "-";

// `row.company_name_en`/`company_name_ar` are the request's PROPOSED values
// (null when that field wasn't edited) — the list shows who/what the request
// is about, i.e. the company's current name, from the nested `row.company`.
const getCompanyName = (row, lang) => {
  const company = row.company || {};
  return lang === "ar"
    ? company.name_ar || company.name_en || company.name || EMPTY_VALUE
    : company.name_en || company.name_ar || company.name || EMPTY_VALUE;
};

const CompanyCell = ({ row, lang }) => {
  const title = getCompanyName(row, lang);

  return (
    <div className="profile-edit-requests-table__company">
      <UIAvatar
        src={row.company?.image_url}
        name={title}
        size="sm"
        className="flex-shrink-0"
      />
      <TruncatedWithTooltip title={title}>
        <span className="MuiDataGrid-cellContent">{title}</span>
      </TruncatedWithTooltip>
    </div>
  );
};

CompanyCell.propTypes = {
  row: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired,
};

const TextCell = ({ value }) => (
  <span className="MuiDataGrid-cellContent">{value}</span>
);

TextCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

// Approve/Reject only live on the details page (row.status === "pending" is
// the only case they'd apply to) — deciding on a request without first
// seeing what changed isn't a call the list view should let you make.
const ProfileEditRequestsTable = ({ rows, messagesBundle }) => {
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);

  const formatCreated = (iso) =>
    dayjs(iso)
      .locale(lang === "ar" ? "ar" : "en")
      .format("DD MMM YYYY");

  // Straight to the existing review/diff page (not a new page — reuses
  // ReviewProfileUpdateRequestPage). State flag lets that page know it came
  // from here, so its breadcrumb shows "Profile Edit Requests" and it
  // returns here (instead of the company page) after a decision.
  const goToDetails = (row) => {
    const companyId = row.company?.id;
    if (companyId == null) return;
    history.push(
      generatePath(ROUTE_PATHS.reviewProfileUpdateRequest, { id: companyId }),
      { fromProfileEditRequests: true },
    );
  };

  const columns = [
    {
      field: "company",
      headerName: messagesBundle.columns.company,
      flex: 1.3,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => <CompanyCell row={params.row} lang={lang} />,
    },
    {
      // `company_owner_name` on the row is the proposed value (null unless
      // that field was edited) — the current owner's name lives on `company`.
      field: "requested_by",
      headerName: messagesBundle.columns.requestedBy,
      flex: 1,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => (
        <TextCell
          value={params.row.company?.company_owner?.name || EMPTY_VALUE}
        />
      ),
    },
    {
      field: "created_at",
      headerName: messagesBundle.columns.requestDate,
      flex: 0.8,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <TextCell
          value={
            params.row.created_at
              ? formatCreated(params.row.created_at)
              : EMPTY_VALUE
          }
        />
      ),
    },
    // A request nobody has opened yet is `pending` ("New"); once an admin opens
    // it, it becomes `in_review` ("Opened") — never shown as pending / in review.
    {
      field: "status",
      headerName: messagesBundle.columns.requestStatus,
      flex: 0.8,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <UIStatusTag
          preset="table"
          status={params.row.status}
          label={
            messagesBundle.statusOptions[params.row.status] ||
            params.row.status
          }
        />
      ),
    },
    {
      // PROLINE-3874 — the API counts it now; never recomputed on the client.
      field: "edited_fields_count",
      headerName: messagesBundle.columns.editedFields,
      flex: 0.8,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <TextCell value={params.row.edited_fields_count ?? EMPTY_VALUE} />
      ),
    },
  ];

  return (
    <div className="profile-edit-requests-table-wrap">
      <UITable
        rows={rows}
        columns={columns}
        contentSized
        hideFooter
        className="profile-edit-requests-table"
        getRowClassName={() => "profile-edit-requests-table__row"}
        onRowClick={(params) => goToDetails(params.row)}
      />
    </div>
  );
};

ProfileEditRequestsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  messagesBundle: PropTypes.object.isRequired,
};

export default ProfileEditRequestsTable;
